import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { ChipModule } from 'primeng/chip';
import { DialogModule } from 'primeng/dialog';
import { DatePickerModule } from 'primeng/datepicker';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { PageDataPanelComponent } from '../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../shared/page-layout/page-hero.component';
import { PageMetric } from '../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../shared/page-layout/page-shell.component';
import { Customer } from '../customer/customer.model';
import { CustomerService } from '../customer/customer.service';
import { ProductivityIncident, ProductivityTask } from './productivity.model';
import { ProductivityService } from './productivity.service';

@Component({
  selector: 'app-productivity-incident-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, DatePipe, ButtonModule, ChipModule, DialogModule, DatePickerModule, IftaLabelModule, InputGroupModule, InputGroupAddonModule, InputNumberModule, InputTextModule, SelectModule, TableModule, TagModule, TextareaModule, PageShellComponent, PageHeroComponent, PageMetricStripComponent, PageDataPanelComponent, PageFormSectionComponent],
  template: `
    <app-page-shell *ngIf="incident">
      <app-page-hero
        eyebrow="Incident Workspace"
        [title]="incident.title || 'Incident'"
        [subtitle]="incident.description || 'Operational issue and resolution workspace.'"
        [auditStamp]="{ createdAt: incident.createdAt, updatedAt: incident.updatedAt }">
        <div nav class="incident-status">
          <a routerLink="/productivity/incidents" class="text-sm text-primary no-underline">Back to incidents</a>
          <span class="status-tags">
            <p-tag [value]="incident.severity || 'MEDIUM'"></p-tag>
            <p-tag [value]="incident.status || 'OPEN'"></p-tag>
          </span>
        </div>
        <div actions>
          <p-button label="Resolve" icon="pi pi-check" severity="success" (onClick)="resolveDialogVisible = true" *ngIf="incident.status !== 'RESOLVED'"></p-button>
          <p-button label="Add Task" icon="pi pi-plus" (onClick)="openTask()"></p-button>
        </div>
      </app-page-hero>

      <app-page-metric-strip [metrics]="incidentMetrics"></app-page-metric-strip>

      <app-page-data-panel title="Resolution Tasks" subtitle="Tasks linked to this incident." [resultText]="tasks.length + ' tasks'">
        <p-table [value]="tasks" [loading]="loadingTasks" styleClass="workspace-table">
          <ng-template pTemplate="header">
            <tr><th>Task</th><th>Assignee</th><th>Due</th><th>Status</th><th class="text-right">Actions</th></tr>
          </ng-template>
          <ng-template pTemplate="body" let-task>
            <tr>
              <td><div class="font-semibold">{{ task.title }}</div><div class="text-sm text-500">{{ task.description }}</div></td>
              <td>
                <p-chip *ngIf="task.assignedCustomer || task.assignedCustomerId; else noAssignee"
                  icon="pi pi-user"
                  [label]="customerLabel(task.assignedCustomer, task.assignedCustomerId)"
                  styleClass="workspace-customer-chip">
                </p-chip>
                <ng-template #noAssignee><span class="text-500">-</span></ng-template>
              </td>
              <td>{{ task.dueDate | date:'mediumDate' }}</td>
              <td><p-select [options]="taskStatuses" [(ngModel)]="task.status" optionLabel="label" optionValue="value" (onChange)="updateTaskStatus(task, $event.value)"></p-select></td>
              <td class="text-right"><p-button icon="pi pi-pencil" [text]="true" (onClick)="openTask(task)"></p-button></td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="5" class="text-center p-4 text-500">No resolution tasks yet.</td></tr>
          </ng-template>
        </p-table>
      </app-page-data-panel>

      <p-dialog header="Resolution Task" [(visible)]="taskDialogVisible" [modal]="true" styleClass="workspace-dialog" [style]="{width: '48rem'}">
        <app-page-form-section icon="pi pi-check-square" title="Resolution Task" subtitle="Add or update the task required to resolve this incident.">
          <div class="dialog-form-grid">
            <div class="dialog-field span-2">
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-check-square"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <input id="resolutionTaskTitle" pInputText [(ngModel)]="taskDraft.title">
                  <label for="resolutionTaskTitle">Title</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="dialog-field span-2">
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-align-left"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <textarea id="resolutionTaskDescription" pTextarea rows="3" [(ngModel)]="taskDraft.description"></textarea>
                  <label for="resolutionTaskDescription">Description</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="dialog-field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-user"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-select inputId="resolutionTaskAssignee" [options]="customerOptions" [ngModel]="selectedAssignee"
                    (ngModelChange)="onAssigneeChange($event)" optionLabel="name" [filter]="true" [lazy]="true"
                    (onShow)="loadCustomerOptions()" (onLazyLoad)="loadCustomerOptions($event)" appendTo="body" styleClass="w-full">
                    <ng-template pTemplate="item" let-customer>
                      <div class="option-stack"><strong>{{ customer.name || ('Customer #' + customer.id) }}</strong><small>{{ customer.emailAddress || customer.phoneNumber || ('ID ' + customer.id) }}</small></div>
                    </ng-template>
                    <ng-template pTemplate="selectedItem" let-customer>
                      <span>{{ customer?.name || (customer?.id ? ('Customer #' + customer.id) : '') }}</span>
                    </ng-template>
                  </p-select>
                  <label for="resolutionTaskAssignee">Assigned customer</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="dialog-field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-datePicker inputId="resolutionTaskDueDate" [(ngModel)]="taskDraft.dueDate" appendTo="body" styleClass="w-full"></p-datePicker>
                  <label for="resolutionTaskDueDate">Due date</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="dialog-field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-flag"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-select inputId="resolutionTaskPriority" [options]="priorityOptions" [(ngModel)]="taskDraft.priority" optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full"></p-select>
                  <label for="resolutionTaskPriority">Priority</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="dialog-field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-circle"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-select inputId="resolutionTaskStatus" [options]="taskStatuses" [(ngModel)]="taskDraft.status" optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full"></p-select>
                  <label for="resolutionTaskStatus">Status</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
          </div>
        </app-page-form-section>
        <ng-template pTemplate="footer">
          <p-button label="Cancel" severity="secondary" (onClick)="taskDialogVisible = false"></p-button>
          <p-button label="Save" icon="pi pi-check" (onClick)="saveTask()"></p-button>
        </ng-template>
      </p-dialog>

      <p-dialog header="Resolve Incident" [(visible)]="resolveDialogVisible" [modal]="true" styleClass="workspace-dialog" [style]="{width: '40rem'}">
        <app-page-form-section icon="pi pi-check-circle" title="Resolution Notes" subtitle="Record the closure details before marking this incident resolved.">
          <div class="dialog-field">
            <p-inputGroup>
              <p-inputGroupAddon><i class="pi pi-verified"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <textarea id="incidentResolutionNotes" pTextarea rows="5" [(ngModel)]="resolutionNotes"></textarea>
                <label for="incidentResolutionNotes">Resolution notes</label>
              </p-iftaLabel>
            </p-inputGroup>
          </div>
        </app-page-form-section>
        <ng-template pTemplate="footer">
          <p-button label="Cancel" severity="secondary" (onClick)="resolveDialogVisible = false"></p-button>
          <p-button label="Resolve" icon="pi pi-check" severity="success" (onClick)="resolveIncident()"></p-button>
        </ng-template>
      </p-dialog>
    </app-page-shell>
  `,
  styles: [`
    :host { display: block; }
    .incident-status,
    .status-tags {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 0.5rem;
    }
    :host ::ng-deep .workspace-table .p-datatable-table { min-width: 54rem; }
    .dialog-form-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 1rem;
    }
    .dialog-field {
      display: flex;
      flex-direction: column;
      gap: 0.45rem;
      min-width: 0;
      color: #334155;
      font-weight: 600;
    }
    .dialog-field > span {
      font-size: 0.85rem;
    }
    .span-2 {
      grid-column: 1 / -1;
    }
    :host ::ng-deep .dialog-field .p-inputtext,
    :host ::ng-deep .dialog-field .p-inputnumber,
    :host ::ng-deep .dialog-field .p-inputnumber-input,
    :host ::ng-deep .dialog-field .p-datepicker,
    :host ::ng-deep .dialog-field .p-select,
    .dialog-field input,
    .dialog-field textarea {
      width: 100%;
    }
    @media (max-width: 640px) {
      .dialog-form-grid { grid-template-columns: 1fr; }
      .span-2 { grid-column: auto; }
    }
  `]
})
export class ProductivityIncidentDetailComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private service = inject(ProductivityService);
  private customerService = inject(CustomerService);
  incident?: ProductivityIncident;
  tasks: ProductivityTask[] = [];
  taskDraft: ProductivityTask = { title: '', status: 'OPEN', priority: 'NORMAL' };
  customerOptions: Customer[] = [];
  selectedAssignee: Customer | null = null;
  taskDialogVisible = false;
  resolveDialogVisible = false;
  loadingTasks = false;
  resolutionNotes = '';
  taskStatuses = ['OPEN', 'IN_PROGRESS', 'BLOCKED', 'COMPLETED', 'CANCELLED', 'NOT_REQUIRED'].map(value => ({ label: value, value }));
  priorityOptions = ['LOW', 'NORMAL', 'HIGH', 'URGENT'].map(value => ({ label: value, value }));

  get incidentMetrics(): PageMetric[] {
    if (!this.incident) return [];
    return [
      { label: 'Owner', value: this.customerLabel(this.incident.ownerCustomer, this.incident.ownerCustomerId), hint: 'Assigned customer' },
      { label: 'Reporter', value: this.customerLabel(this.incident.reporterCustomer, this.incident.reporterCustomerId), hint: 'Reported by' },
      { label: 'Resource', value: `${this.incident.resourceKey || '-'} ${this.incident.resourceId || ''}`.trim(), hint: 'Linked workspace item' },
      { label: 'Updated', value: this.incident.updatedAt ? new Date(this.incident.updatedAt).toLocaleDateString() : '-', hint: 'Latest change' }
    ];
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    this.service.getIncident(id).subscribe(res => {
      this.incident = res.data;
      this.loadTasks();
    });
  }

  loadTasks(): void {
    if (!this.incident?.id) return;
    this.loadingTasks = true;
    this.service.getTasks({ incidentId: this.incident.id }).subscribe({
      next: res => { this.tasks = res.data || []; this.loadingTasks = false; },
      error: () => this.loadingTasks = false
    });
  }

  openTask(task?: ProductivityTask): void {
    this.taskDraft = task ? { ...task } : {
      title: '',
      status: 'OPEN',
      priority: 'NORMAL',
      incidentId: this.incident?.id,
      assignedCustomerId: this.incident?.ownerCustomerId || this.service.getCustomerId(),
      resourceKey: 'PRODUCTIVITY_INCIDENT',
      resourceId: this.incident?.id
    };
    this.syncAssigneeSelection();
    this.taskDialogVisible = true;
  }

  saveTask(): void {
    this.service.saveTask({ ...this.taskDraft, incidentId: this.incident?.id }).subscribe(() => {
      this.taskDialogVisible = false;
      this.service.refreshSummary();
      this.loadTasks();
    });
  }

  updateTaskStatus(task: ProductivityTask, status: string): void {
    if (!task.id) return;
    this.service.updateTaskStatus(task.id, status).subscribe(() => this.service.refreshSummary());
  }

  resolveIncident(): void {
    if (!this.incident?.id) return;
    this.service.resolveIncident(this.incident.id, this.resolutionNotes).subscribe(res => {
      this.incident = res.data;
      this.resolveDialogVisible = false;
      this.service.refreshSummary();
    });
  }

  loadCustomerOptions(event?: any): void {
    this.customerService.getCustomersByCompany(this.service.getCompanyId()).subscribe(customers => {
      const search = String(event?.filter || '').toLowerCase();
      this.customerOptions = (customers || []).filter(customer => {
        const label = `${customer.name || ''} ${customer.emailAddress || ''} ${customer.phoneNumber || ''} ${customer.id || ''}`.toLowerCase();
        return !search || label.includes(search);
      });
      this.syncAssigneeSelection();
    });
  }

  onAssigneeChange(customer: Customer | null): void {
    this.selectedAssignee = customer;
    this.taskDraft.assignedCustomerId = customer?.id;
  }

  customerLabel(customer?: Customer, fallbackId?: number): string {
    return customer?.name || customer?.emailAddress || (fallbackId ? `Customer #${fallbackId}` : '-');
  }

  private syncAssigneeSelection(): void {
    const id = this.taskDraft.assignedCustomerId;
    this.selectedAssignee = id
      ? this.customerOptions.find(customer => customer.id === id) || ({ id, name: `Customer #${id}` } as Customer)
      : null;
  }
}
